import { useId, useState, type ChangeEvent, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import styles from "./TextField.module.css";
import { cx } from "../cx";

type Common = {
  label: string;
  description?: string;
  error?: string;
  maxLength?: number;
  showCounter?: boolean;
  className?: string;
  value: string;
  onValueChange: (value: string) => void;
};

type SingleProps = Common & { multiline?: false } & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "className" | "maxLength"
  >;
type MultiProps = Common & { multiline: true } & Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    "value" | "onChange" | "className" | "maxLength"
  >;

export type TextFieldProps = SingleProps | MultiProps;

/**
 * SEED TextField / TextArea (Figma 59:1066).
 * States: empty · focused · typing · filled · error — derived from value/focus/error.
 */
export function TextField(props: TextFieldProps) {
  const { label, description, error, maxLength, showCounter = true, className, value, onValueChange, multiline: _multiline, ...rest } = props;
  const id = useId();
  const [focused, setFocused] = useState(false);
  const hasError = Boolean(error);

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
    onValueChange(next);
  };

  const fieldClass = cx(styles.input, focused && styles.focused, hasError && styles.error, props.multiline && styles.multiline);

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.labelRow}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        {description && <span className={styles.description}>{description}</span>}
      </div>
      {props.multiline ? (
        <textarea
          id={id}
          className={fieldClass}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={hasError || undefined}
          {...(rest as Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value">)}
        />
      ) : (
        <input
          id={id}
          className={fieldClass}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-invalid={hasError || undefined}
          {...(rest as Omit<InputHTMLAttributes<HTMLInputElement>, "value">)}
        />
      )}
      <div className={styles.footer}>
        <span className={cx(styles.message, hasError && styles.errorMessage)}>{error ?? " "}</span>
        {showCounter && maxLength !== undefined && (
          <span className={cx(styles.counter, focused && !hasError && styles.counterActive)}>
            {value.length.toLocaleString("ko-KR")} / {maxLength.toLocaleString("ko-KR")}
          </span>
        )}
      </div>
    </div>
  );
}
